// ============================================================
// OCCLUSION ON THE DISPLAY - who stands behind what, without flicker.
//
// The display draws every figure OVER the terrain (the classic map's rule:
// sorting figures in among faces swapped them as the camera drifted, and
// flickered). A figure standing behind a solid is therefore drawn through
// it, dashed and dim - which needs to know when it is behind one.
//
// The answer is a fraction of sample points (feet to head) covered by a
// nearer face, and the switch has HYSTERESIS: on at OCCLUDE_ON, off at
// OCCLUDE_OFF. A figure at a wall's edge, whose fraction wobbles as the
// breathing camera drifts, keeps the state it had - which is what the demo
// lacked and the user saw as flicker (2026-10-07).
//
// Screen-space, with each face's depth taken AT the pixel from the plane
// through its projected corners - exact for flat faces under the
// orthographic camera (perspective is a Track C follow-up). Pure. Zero
// React dependency.
// ============================================================

/** At or above this fraction hidden, a visible figure becomes see-through. */
export const OCCLUDE_ON = 0.6;
/** At or below this fraction hidden, a see-through figure becomes plain again. */
export const OCCLUDE_OFF = 0.4;

import { depthKey, project } from '../battlefield/projection';
import type { Camera, Viewport } from '../battlefield/projection';
import type { Vec3 } from '../battlefield/types';

export interface DepthPoint {
  x: number;
  y: number;
  /** Larger is further from the viewer, as `project` returns it. */
  depth: number;
  /** Under perspective, as `project` returns it: the depth at a pixel is compared through `depthKey`. */
  scale?: number;
}

/** What occlusion needs of a face: the solid it belongs to, its screen polygon and each corner's depth. */
export interface OccludingFace {
  solidId: string;
  points: { x: number; y: number }[];
  /** In step with `points`, each as `depthKey` gives it. */
  pointDepths: number[];
}

/** Even-odd point-in-polygon. */
function covers(points: { x: number; y: number }[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i];
    const b = points[j];
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** A face's screen box, its nearest corner, and the plane its depth lies in: computed once per face list. */
type Prepared = { x0: number; y0: number; x1: number; y1: number; nearest: number; plane?: Plane | null };
/** key = a·x + b·y + c over the screen: exact for a flat face, the key being `depthKey`'s. */
type Plane = { a: number; b: number; c: number };
const preparedCache = new WeakMap<readonly OccludingFace[], Prepared[]>();

/** The plane through the face's first three corners that are not in a line, or null for a sliver. */
function planeOf(f: OccludingFace): Plane | null {
  const [p, q] = f.points;
  const [dp, dq] = f.pointDepths;
  for (let k = 2; k < f.points.length; k++) {
    const r = f.points[k];
    const dr = f.pointDepths[k];
    const det = (q.x - p.x) * (r.y - p.y) - (r.x - p.x) * (q.y - p.y);
    if (Math.abs(det) < 1e-9) continue;
    const a = ((dq - dp) * (r.y - p.y) - (dr - dp) * (q.y - p.y)) / det;
    const b = ((dr - dp) * (q.x - p.x) - (dq - dp) * (r.x - p.x)) / det;
    return { a, b, c: dp - a * p.x - b * p.y };
  }
  return null;
}

/** Box and nearest corner in one pass; the plane waits until a point actually lands in the box. */
function prepareOne(f: OccludingFace): Prepared {
  const b: Prepared = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity, nearest: Infinity };
  f.points.forEach((p, i) => {
    b.x0 = Math.min(b.x0, p.x);
    b.y0 = Math.min(b.y0, p.y);
    b.x1 = Math.max(b.x1, p.x);
    b.y1 = Math.max(b.y1, p.y);
    b.nearest = Math.min(b.nearest, f.pointDepths[i]);
  });
  return b;
}

function prepare(faces: readonly OccludingFace[]): Prepared[] {
  const hit = preparedCache.get(faces);
  if (hit) return hit;
  const built = faces.map(prepareOne);
  preparedCache.set(faces, built);
  return built;
}

const inBox = (b: Prepared, x: number, y: number) => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1;

/** Is the face in front of the point at the point's own pixel? A sliver with no plane answers by its nearest corner. */
function inFront(face: OccludingFace, f: Prepared, point: DepthPoint, key: number): boolean {
  if (f.plane === undefined) f.plane = planeOf(face);
  const at = f.plane ? f.plane.a * point.x + f.plane.b * point.y + f.plane.c : f.nearest;
  return at < key - 1e-6;
}

/**
 * Is the point behind something: a face that covers it on screen AND lies in
 * front of it at that pixel? The depth is the face's own at the pixel, not
 * its centre's: a figure on the back of a roof stands above the roof there
 * (review of Track A). Stops at the first face that hides it; faces whose
 * nearest corner is behind the point, or whose box misses it, are skipped
 * before the polygon test.
 */
export function isHidden(point: DepthPoint, faces: readonly OccludingFace[]): boolean {
  const prepared = prepare(faces);
  const key = depthKey(point);
  return faces.some((f, i) => hides(f, prepared[i], point, key));
}

/** Does this one face hide the point at its pixel? The cheap box tests first, then the depth and the polygon. */
function hides(face: OccludingFace, p: Prepared, point: DepthPoint, key: number): boolean {
  if (p.nearest >= key || !inBox(p, point.x, point.y)) return false;
  return inFront(face, p, point, key) && covers(face.points, point.x, point.y);
}

/** The solids with a face in front of the point at its pixel: what the x-ray draws as wireframe. */
export function hidersOf(point: DepthPoint, faces: readonly OccludingFace[]): Set<string> {
  const prepared = prepare(faces);
  const key = depthKey(point);
  const out = new Set<string>();
  faces.forEach((f, i) => {
    if (!out.has(f.solidId) && hides(f, prepared[i], point, key)) out.add(f.solidId);
  });
  return out;
}

/** The share of the samples hidden behind some nearer face, in [0, 1]. */
export function hiddenFraction(samples: readonly DepthPoint[], faces: readonly OccludingFace[]): number {
  if (!samples.length) return 0;
  const hidden = samples.filter((p) => isHidden(p, faces)).length;
  return hidden / samples.length;
}

/** The next see-through state, given the last one and the fraction hidden now. */
export function nextOccluded(was: boolean, fraction: number): boolean {
  return was ? fraction > OCCLUDE_OFF : fraction >= OCCLUDE_ON;
}

/** For each solid, the share of these samples it hides. */
function sharesOf(samples: readonly DepthPoint[], faces: readonly OccludingFace[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const p of samples) for (const id of hidersOf(p, faces)) counts.set(id, (counts.get(id) ?? 0) + 1);
  return new Map([...counts].map(([id, n]) => [id, n / samples.length]));
}

/**
 * The x-ray's next wireframes ("never lose the actor"): a solid hiding at
 * least OCCLUDE_ON of some figure's samples joins; one already drawn so stays
 * while it hides more than OCCLUDE_OFF of one - the same hysteresis as the
 * see-through figures, so a figure at a wall's edge does not flicker it.
 */
export function nextXray(
  was: ReadonlySet<string>,
  figures: readonly (readonly DepthPoint[])[],
  faces: readonly OccludingFace[],
): Set<string> {
  const share = new Map<string, number>();
  for (const samples of figures) {
    for (const [id, f] of sharesOf(samples, faces)) share.set(id, Math.max(share.get(id) ?? 0, f));
  }
  return new Set([...share].filter(([id, f]) => nextOccluded(was.has(id), f)).map(([id]) => id));
}

/**
 * WHAT CAN HIDE A FIGURE, for one camera frame (perf overhaul 2b): the svg map answers from its projected faces
 * (`faceHider`, here), the GPU map from the solids themselves (`worldHider`, engine/display/gpu/worldOcclusion.ts).
 * The figures' see-through state and the x-ray ask this and never which renderer drew the terrain.
 */
export interface Hider {
  /** The share of these world samples (a figure, feet to head) hidden, in [0, 1]. */
  fraction(samples: readonly Vec3[]): number;
  /** The x-ray's next wireframes for these figures' samples, with the same hysteresis as the figures. */
  xray(was: ReadonlySet<string>, figures: readonly (readonly Vec3[])[]): Set<string>;
}

/** The svg's hider: these faces as drawn under this camera; null when there are none (nothing can hide anyone). */
export function faceHider(faces: readonly OccludingFace[], camera: Camera, view: Viewport): Hider | null {
  if (!faces.length) return null;
  const projected = (samples: readonly Vec3[]) => samples.map((p) => project(p, camera, view));
  return {
    fraction: (samples) => hiddenFraction(projected(samples), faces),
    xray: (was, figures) => nextXray(was, figures.map(projected), faces),
  };
}
